import React, { useState, useEffect } from 'react';
import { 
  FiX, FiServer, FiUsers, FiCheck, FiInfo, FiLayers, FiAlertCircle, FiClock
} from 'react-icons/fi';

const LabBatchSetupModal = ({
  isOpen,
  onClose,
  classId,
  className,
  labSubjects = [],
  onComplete,
  onLabBatchComplete,
  createLabBatches,
  mode = 'post-creation', // 'pre-creation' | 'post-creation'
  notify,
  isSemesterBased = true, // true for semester, false for term
  semesterId = null,
  termId = null,
  semesterNumber = null,
  termNumber = null
}) => {
  // --- State ---
  const [batchCount, setBatchCount] = useState(2);
  const [defaultCapacity, setDefaultCapacity] = useState(30);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setBatchCount(2);
      setDefaultCapacity(30);
      setErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  // --- Handlers ---
  const handleCreateBatches = async () => {
    setErrors({});
    
    // 1. Validation on Submit
    const newErrors = {};
    const countVal = parseInt(batchCount);
    const capVal = parseInt(defaultCapacity);

    if (isNaN(countVal) || countVal <= 0) {
      newErrors.batchCount = 'Please add at least 1 group';
    }
    if (isNaN(capVal) || capVal <= 0) {
      newErrors.defaultCapacity = 'Seats must be greater than 0';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    const config = {
      batchCount: countVal,
      defaultCapacity: capVal,
      batchPrefix: 'Batch', // Default prefix
      // Include academic period info
      isSemesterBased,
      semesterId,
      termId,
      semesterNumber,
      termNumber
    };

    try {
      if (mode === 'pre-creation') {
        // Mode A: Return config to parent (Class Creation Form)
        if (onLabBatchComplete) {
          onLabBatchComplete(true, config);
        }
        onClose();
      } else {
        // Mode B: Execute Creation (Standalone)
        if (typeof createLabBatches === 'function') {
          await createLabBatches(
            classId, 
            config.batchCount, 
            config.batchPrefix,
            config.defaultCapacity,
            config // Pass the full config with period info
          );
          if (notify) notify(`Successfully created ${config.batchCount} lab groups`, 'success');
          if (onComplete) onComplete();
          if (onLabBatchComplete) onLabBatchComplete(true, config);
          onClose();
        } else {
          console.error('createLabBatches function is missing');
        }
      }
    } catch (error) {
      console.error('Batch setup error:', error);
      if (notify) notify('Failed to create lab groups', 'error');
      if (mode === 'pre-creation' && onLabBatchComplete) {
        onLabBatchComplete(false, null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get period display text
  const periodDisplay = isSemesterBased 
    ? `Semester ${semesterNumber || ''}` 
    : `Term ${termNumber || ''}`;

  return (
    /* z-[9999] forces this view to sit strictly on top of all existing page headers or layout panels */
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999] transition-all">
      
      {/* Mobile Bottom App Drawer View / Desktop Centered Box Panel */}
      <div className="bg-slate-100 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[92vh] sm:max-h-[95vh] overflow-hidden flex flex-col transform transition-transform border border-slate-200">
        
        {/* Mobile Swipe Drag Bar Visual */}
        <div className="w-12 h-1.5 bg-slate-300 rounded-full mx-auto my-2.5 block sm:hidden flex-shrink-0"></div>

        {/* HEADER BLOCK */}
        <div className="bg-gradient-to-r from-slate-950 to-indigo-950 text-white p-5 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <FiServer className="text-amber-400" size={15} />
                Lab Groups Setup
              </h2>
              <p className="text-slate-300 text-xs font-medium mt-0.5">
                Set up practical lab groups for <span className="text-white font-bold">{className}</span>
              </p>
            </div>
            <button 
              onClick={onClose} 
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* SCROLLABLE INNER CONTENT */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4 bg-slate-100">
          
          {/* 1. Practical Lab Info Panel */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs flex items-start gap-3">
            <FiInfo className="text-indigo-600 mt-0.5 shrink-0" size={16} />
            <div className="text-xs text-slate-700 font-medium">
              <p className="font-bold text-slate-900 mb-0.5">
                {labSubjects.length} Lab Subject{labSubjects.length !== 1 ? 's' : ''} Found
              </p>
              <p className="leading-relaxed">
                These rules will apply to all lab subjects in this class. Students will be divided across groups based on total seats.
              </p>
            </div>
          </div>

          {/* 2. Academic Block Information */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 shadow-xs flex items-center gap-2.5">
            <FiClock className="text-amber-700 shrink-0" size={15} />
            <div className="text-xs font-bold text-slate-700">
              <span>Time Block: </span>
              <span className="text-amber-900">
                {isSemesterBased ? 'Semester Plan' : 'Term Plan'} &bull; {periodDisplay}
              </span>
            </div>
          </div>

          {/* 3. Group Setup Configuration Inputs */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-4">
            <div className="grid grid-cols-2 gap-4">
              
              {/* Batch Count Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block">
                  Number of Groups
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={batchCount}
                    onChange={(e) => {
                      setBatchCount(e.target.value);
                      if(errors.batchCount) setErrors(prev => ({...prev, batchCount: null}));
                    }}
                    placeholder="e.g. 2"
                    className={`
                      w-full bg-slate-50 border rounded-lg py-2.5 pl-3 pr-9 text-sm font-bold text-slate-900 
                      focus:bg-white focus:ring-2 focus:ring-indigo-100 transition-all outline-none
                      ${errors.batchCount ? 'border-rose-300 focus:border-rose-500' : 'border-slate-300 focus:border-indigo-600'}
                    `}
                  />
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <FiLayers className="text-slate-400 w-4 h-4" />
                  </div>
                </div>
                {errors.batchCount && (
                  <p className="text-xs text-rose-600 mt-1 font-semibold flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" /> {errors.batchCount}
                  </p>
                )}
              </div>

              {/* Individual Capacity Field */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block">
                  Seats per Group
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={defaultCapacity}
                    onChange={(e) => {
                      setDefaultCapacity(e.target.value);
                      if(errors.defaultCapacity) setErrors(prev => ({...prev, defaultCapacity: null}));
                    }}
                    placeholder="e.g. 30"
                    className={`
                      w-full bg-slate-50 border rounded-lg py-2.5 pl-3 pr-9 text-sm font-bold text-slate-900 
                      focus:bg-white focus:ring-2 focus:ring-indigo-100 transition-all outline-none
                      ${errors.defaultCapacity ? 'border-rose-300 focus:border-rose-500' : 'border-slate-300 focus:border-indigo-600'}
                    `}
                  />
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <FiUsers className="text-slate-400 w-4 h-4" />
                  </div>
                </div>
                {errors.defaultCapacity && (
                  <p className="text-xs text-rose-600 mt-1 font-semibold flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" /> {errors.defaultCapacity}
                  </p>
                )}
              </div>
            </div>

            {/* 4. Calculation Preview Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Group Total Preview</span>
                <span className="text-[10px] font-mono font-bold text-slate-400">NAMES: "Batch 1, Batch 2..."</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-2xl font-black text-slate-900">
                    {(parseInt(batchCount) || 0) * (parseInt(defaultCapacity) || 0)}
                  </span>
                  <span className="text-[11px] font-bold text-slate-500">Total Group Capacity</span>
                </div>
                <div className="h-8 w-px bg-slate-300 mx-3"></div>
                <div className="flex flex-col items-end justify-center">
                  <span className="text-xs font-black text-slate-800">{batchCount || 0} Total Groups</span>
                  <span className="text-[11px] font-medium text-slate-500">x {defaultCapacity || 0} Seats Each</span>
                </div>
              </div>
            </div>

            {/* Application Mode Warning */}
            <div className="text-[11px] text-slate-500 font-medium text-center bg-slate-50 border border-slate-200 rounded-lg p-2">
              {mode === 'pre-creation' 
                ? 'These group choices will save and apply when the class is saved.'
                : 'These groups will build and go live instantly in the system.'
              }
            </div>

          </div>
        </div>

        {/* BOTTOM ACTION BUTTONS FOOTER */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2.5 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 border border-slate-300 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateBatches}
            disabled={loading}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-indigo-600/10 flex items-center gap-1.5 transition-all"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white"></div>
                Setting Up...
              </>
            ) : (
              <>
                <FiCheck size={14} />
                {mode === 'pre-creation' ? 'Confirm Settings' : 'Create Groups Now'}
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default LabBatchSetupModal;